import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { SharedModule } from '@shared/shared.module';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzCascaderModule } from 'ng-zorro-antd/cascader';
import { AuthService } from 'src/app/Auth/AuthService';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { HasUnsavedChanges } from 'src/app/Auth/unsaved-changes.guard';
import { ImageCropperComponent, ImageCroppedEvent, LoadedImage } from 'ngx-image-cropper';
import { SecureStorageService } from '@services/secure-storage.service';
import { CompanionCardComponent } from '../companion/companion-card.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzSelectModule,
    NzCascaderModule,
    SharedModule,
    NzModalModule,
    ImageCropperComponent,
    CompanionCardComponent,
  ],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit, HasUnsavedChanges {
  @ViewChild('exampleModal') exampleModal!: ElementRef;
  pagesService = inject(PagesService);
  private modal = inject(NzModalService);
  profileForm: FormGroup;
  resetPasswordForm: FormGroup;
  sharedService = inject(SharedService);
  secureStorage = inject(SecureStorageService);
  translateService = inject(TranslateService);
  currentImage: any;
  companyData: any;
  imageSrc: any;
  selectedValue = 'User';
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  isPasswordVisible = false;
  hasCompanionAccess: boolean = false;

  // Image cropper variables
  cropperModalVisible = false;
  imageChangedEvent: Event | null = null;
  croppedImage: string = '';

  // Edit mode variables
  isEditMode = false;
  userName: string = '';
  userEmail: string = '';
  userPhone: string = '';
  businessName: string = '';
  editableName: string = '';
  editableBusinessName: string = '';
  businessDescription: string = '';
  originalBusinessDescription: string = '';
  isDescriptionChanged: boolean = false;
  businessSector: any[] = [];
  originalBusinessSector: any[] = [];
  sectorsList: any[] = [];
  sectorsTree: any[] = [];
  private sectorNameMap = new Map<number, string>();
  sectorMenuStyle: { [key: string]: string } = {};
  @ViewChild('businessSectorCascader', { read: ElementRef })
  businessSectorCascader?: ElementRef<HTMLElement>;

  // Password strength tracking - initially all false
  passwordStrength = {
    lowercase: false,
    uppercase: false,
    number: false,
    specialChar: false,
    minLength: false,
  };

  private checkCompanionAccess(data: any): boolean {
    if (!data) return false;
    return (
      data?.campaign_companion === true ||
      data?.companion_access === true ||
      data?.whatsapp_companion === true ||
      data?.whatsapp_access === true ||
      data?.companion_features === true ||
      (data?.sub_plan?.campaign_companion ??
       data?.sub_plan?.companion_access ??
       data?.sub_plan?.whatsapp_companion ??
       data?.sub_plan?.whatsapp_access ??
       data?.sub_plan?.companion_features) === true
    );
  }

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router
  ) {
    this.profileForm = this.fb.group({
      business_logo: [''],
      name: ['', Validators.required],
      address: [''],
      userDetail: this.fb.array([this.createUserDetail()]),
      password: [
        '',
        [
          Validators.minLength(8),
          Validators.pattern(
            /^(?=.*[A-Za-z])(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,16}$/
          ),
        ],
      ],
    });

    // ✅ Enhanced reset password form with custom validator
    this.resetPasswordForm = this.fb.group(
      {
        oldPassword: ['', Validators.required],
        new_password: ['', [Validators.required]],
        confirmPassword: ['', Validators.required],
      },
      { validators: this.passwordMatchValidator } // ✅ Add custom validator
    );
  }
  profileImage: any

  // Image Modal Properties
  imageModalVisible: boolean = false;
  imageModalImages: string[] = [];
  imageModalCurrentIndex: number = 0;

  ngOnInit() {
    this.hasCompanionAccess = this.secureStorage.getItem<boolean>('hasCompanionAccess') || false;

    const userDetails = this.authService.getUserDetails();
    if (userDetails && this.checkCompanionAccess(userDetails)) {
      this.hasCompanionAccess = true;
    }

    this.sharedService.updatedUserData.subscribe((planData: any) => {
      if (planData && typeof planData === 'object') {
        const hasAccess = this.checkCompanionAccess(planData);
        this.hasCompanionAccess = hasAccess;
        void this.secureStorage.setItem('hasCompanionAccess', hasAccess);
      }
    });

    // Get company details which also loads user data from _user object
    this.getCompanyDetail();
    this.loadSectors();

    this.sharedService.profileImage$.subscribe((img) => {
      if (img) {
        this.profileImage = img;
      }
    });

    // Initialize password strength as false (unchecked)
    this.initializePasswordStrength();
  }

  // Load user data from API (called after getCompanyDetail)
  loadUserData() {
    // This will be populated from API response in getCompanyDetail
    // Keeping method for backward compatibility
  }

  // Toggle edit mode
  toggleEditMode() {
    // Check if company section is being edited
    if (this.isDescriptionChanged) {
      this.sharedService.createNotification('warning', this.translateService.instant('profile-page.save-cancel-company-first'));
      return;
    }

    this.isEditMode = !this.isEditMode;
    if (this.isEditMode) {
      this.editableName = this.userName;
      this.editableBusinessName = this.businessName;
    }
  }

  // Cancel profile edit
  cancelProfileEdit() {
    this.isEditMode = false;
    this.editableName = this.userName;
    this.editableBusinessName = this.businessName;
    // Reset image if it was changed but not saved
    this.getCompanyDetail();
  }

  // Track business info changes
  onCompanyNameChange() {
    const sectorChanged = JSON.stringify(this.businessSector) !== JSON.stringify(this.originalBusinessSector);
    this.isDescriptionChanged =
      this.businessDescription !== this.originalBusinessDescription || sectorChanged;
  }

  // Cancel company name edit
  cancelCompanyNameEdit() {
    this.businessDescription = this.originalBusinessDescription;
    this.businessSector = [...this.originalBusinessSector];
    this.isDescriptionChanged = false;
  }

  // Save changes with API call
  saveProfileChanges() {
    if (!this.editableName.trim()) {
      this.sharedService.createNotification('error', this.translateService.instant('profile-page.name-empty'));
      return;
    }

    this.sharedService.showLoader();

    // Prepare payload for company profile update
    if (this.companyData?.id) {
      const businessLogoPayload = this.prepareBusinessLogoPayload();

      const payload = {
        business_logo: businessLogoPayload,
        name: (this.editableBusinessName || this.businessName).trim(),
        details: this.companyData.details || this.companyData.description || this.companyData.address || this.businessDescription,
        sector_id: this.extractSectorIds(this.businessSector),
        _user: {
          id: this.companyData._user?.id,
          name: this.editableName.trim(),
          email: this.companyData._user?.email,
        },
        company_as_client_0_id: this.companyData.id,
      };

      this.pagesService
        .patch('SET_COMPANY_DETAILS', this.companyData.id, payload)
        .subscribe({
          next: (response) => {
            this.sharedService.hideLoader();

            // Update local variables
            this.userName = this.editableName;
            if (this.editableBusinessName) {
              this.businessName = this.editableBusinessName;
            }

            // Update localStorage (user name) - for backward compatibility
            // Note: User name will come from API _user object on next load
            const userDetails = this.authService.getUserDetails() || {};
            userDetails.name = this.userName;
            void this.secureStorage.setItem('userDetails', userDetails);

            // ✅ Preserve profile image in localStorage
            if (this.imageSrc && this.imageSrc !== 'assets/images/profile.png') {
              localStorage.setItem('profileImage', this.imageSrc);
              this.sharedService.updateProfileImage(this.imageSrc);
            }

            this.isEditMode = false;
            this.sharedService.createNotification('success', this.translateService.instant('profile-page.profile-update-success'));

            // Refresh company details
            this.getCompanyDetail();
          },
          error: (error) => {
            this.sharedService.hideLoader();
            console.error('Failed to update profile:', error);
            this.sharedService.createNotification('error', this.translateService.instant('profile-page.profile-update-failed'));
          },
        });
    } else {
      this.sharedService.hideLoader();
      this.sharedService.createNotification('error', this.translateService.instant('profile-page.company-not-found'));
    }
  }

  // Update company information (Business Description & Sector)
  updateCompanyInfo() {
    if (!this.businessDescription.trim()) {
      this.sharedService.createNotification('error', this.translateService.instant('profile-page.business-desc-empty'));
      return;
    }

    this.sharedService.showLoader();

    if (this.companyData?.id) {
      const businessLogoPayload = this.prepareBusinessLogoPayload();

      const payload = {
        business_logo: businessLogoPayload,
        name: this.companyData.name || this.businessName,
        details: this.businessDescription,
        sector_id: this.extractSectorIds(this.businessSector),
        _user: {
          id: this.companyData._user?.id,
          name: this.companyData._user?.name || this.userName,
          email: this.companyData._user?.email,
        },
        company_as_client_0_id: this.companyData.id,
      };

      this.pagesService
        .patch('SET_COMPANY_DETAILS', this.companyData.id, payload)
        .subscribe({
          next: (response) => {
            this.sharedService.hideLoader();
            this.originalBusinessDescription = this.businessDescription;
            this.originalBusinessSector = [...this.businessSector];
            this.isDescriptionChanged = false;

            // ✅ Preserve profile image in localStorage after company update
            if (this.imageSrc && this.imageSrc !== 'assets/images/profile.png') {
              localStorage.setItem('profileImage', this.imageSrc);
            }

            this.sharedService.createNotification('success', this.translateService.instant('profile-page.company-update-success'));

            // Refresh to get latest data
            this.getCompanyDetail();
          },
          error: (error) => {
            this.sharedService.hideLoader();
            console.error('Failed to update company information:', error);
          },
        });
    } else {
      this.sharedService.hideLoader();
      this.sharedService.createNotification('error', this.translateService.instant('profile-page.company-not-found'));
    }
  }
  showOldPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  // Password change flow variables
  isExistingPasswordVerified = false;
  existingPasswordValue = '';
  isVerifyingPassword = false;


  // Initialize all password strength conditions as false
  initializePasswordStrength() {
    this.passwordStrength = {
      lowercase: false,
      uppercase: false,
      number: false,
      specialChar: false,
      minLength: false,
    };
  }

  // ✅ Improved getCompanyDetail to properly sync with backend

  async getCompanyDetail() {
    this.sharedService.showLoader();
    try {
      const response: any = await this.pagesService
        .getAll('GET_USER_COMPANY')
        .toPromise();

      if (response) {
        const userData = response[0] || {};
        this.companyData = userData;
        this.businessName = userData.name || 'Business Name';
        this.businessDescription =
          userData.details ||
          userData.description ||
          userData.business_description ||
          userData.businessDescription ||
          userData.address ||
          '';
        this.originalBusinessDescription = this.businessDescription;
        this.isDescriptionChanged = false;

        // Build sector cascader value from API response
        const sectorData = userData.business_sector || userData.sector_id || [];
        this.businessSector = this.buildSectorCascaderValue(sectorData);
        this.originalBusinessSector = [...this.businessSector];

        // ✅ Extract user data from _user object
        // Get phone number from multiple possible sources
        const userDetails = this.authService.getUserDetails() || {};
        this.userPhone =
          userData._user?.phone_number ||
          userData.phone_number ||
          userDetails?.phone_number ||
          userDetails?.phoneNumber ||
          userDetails?.isCompany?.[0]?._user?.phone_number ||
          userDetails?.isCompany?.[0]?.phone_number ||
          '';

        if (userData._user) {
          this.userName = userData._user.name || 'User';
          this.userEmail = userData._user.email || '';
          this.editableName = this.userName;
        } else {
          // Fallback to localStorage if _user not available
          this.userName = userDetails?.name || 'User';
          this.userEmail = userDetails?.email || '';
          this.editableName = this.userName;
        }

        if (
          this.checkCompanionAccess(userData) ||
          this.checkCompanionAccess(userData._user) ||
          this.checkCompanionAccess(userDetails)
        ) {
          this.hasCompanionAccess = true;
          void this.secureStorage.setItem('hasCompanionAccess', true);
        }

        this.profileForm.patchValue({
          business_logo: userData.business_logo?.[0] || null,
          name: userData.name || '',
          address: userData.address || '',
          password: '',
        });

        // ✅ IMPROVED: Preserve image even when backend returns null
        if (userData.business_logo?.length && userData.business_logo[0].url) {
          // Backend has image - USE IT and SAVE IT
          this.imageSrc = userData.business_logo[0].url;
          localStorage.setItem('profileImage', this.imageSrc);
          this.sharedService.updateProfileImage(this.imageSrc);
        } else {
          // Backend returned null - check localStorage to preserve existing image
          const storedImage = localStorage.getItem('profileImage');

          if (storedImage && storedImage !== 'assets/images/profile.png' && !storedImage.includes('assets/images/profile')) {
            // Use existing stored image (preserve it)
            this.imageSrc = storedImage;
            this.sharedService.updateProfileImage(storedImage);
          } else {
            // No stored image, set default
            this.imageSrc = 'assets/images/profile.png';
          }
        }
      }
    } catch (error) {
      this.sharedService.createNotification(
        'error',
        this.translateService.instant('profile-page.load-profile-failed')
      );
      console.error('Error loading user profile:', error);
    } finally {
      this.sharedService.hideLoader();
    }
  }

  // Load sectors from API
  loadSectors(): void {
    this.pagesService.getAll('GET_SECTORS').subscribe({
      next: (response: any) => {
        this.sectorsList = Array.isArray(response)
          ? response
          : Array.isArray(response?.items)
            ? response.items
            : Array.isArray(response?.data)
              ? response.data
              : [];
        this.sectorNameMap.clear();
        this.sectorsList.forEach((item: any) => {
          const id = Number(item?.id);
          if (Number.isFinite(id)) {
            this.sectorNameMap.set(id, item?.name || String(id));
          }
        });
        this.sectorsTree = this.buildSectorTree(this.sectorsList);
      },
      error: (err) => {
        console.error('Failed to load sectors', err);
      }
    });
  }

  buildSectorTree(flatList: any[]): any[] {
    const map = new Map<number, any>();
    const roots: any[] = [];

    // Only show active sectors — inactive ones must not appear in the settings page
    const activeList = flatList.filter((item: any) => item.is_active);

    activeList.forEach(item => {
      map.set(item.id, {
        value: item.id,
        label: item.name,
        children: [],
        isLeaf: true
      });
    });

    activeList.forEach(item => {
      const node = map.get(item.id);
      if (!item.parent_id || item.parent_id === 0) {
        roots.push(node);
      } else {
        const parent = map.get(item.parent_id);
        if (parent) {
          parent.children.push(node);
          parent.isLeaf = false;
        }
      }
    });

    const cleanLeaves = (nodes: any[]) => {
      nodes.forEach(node => {
        if (node.children.length === 0) {
          delete node.children;
        } else {
          cleanLeaves(node.children);
        }
      });
    };
    cleanLeaves(roots);

    return roots;
  }

  // Extract leaf sector IDs from cascader value
  extractSectorIds(cascaderValue: any): number[] | null {
    if (!cascaderValue || !Array.isArray(cascaderValue) || cascaderValue.length === 0) {
      return null;
    }
    return cascaderValue.map((path: any): number => {
      if (Array.isArray(path) && path.length > 0) {
        return path[path.length - 1] as number;
      }
      return path as number;
    });
  }

  // Build cascader-compatible value from API sector data
  buildSectorCascaderValue(sectorData: any): any[] {
    if (!sectorData) return [];

    // If it's an array of IDs or objects
    let sectorIds: number[] = [];
    if (Array.isArray(sectorData)) {
      sectorIds = sectorData.map((s: any) => typeof s === 'object' ? (s.id || s.value) : s);
    } else if (typeof sectorData === 'number') {
      sectorIds = [sectorData];
    } else if (typeof sectorData === 'string' && sectorData) {
      // Try to parse if it's a JSON string
      try {
        const parsed = JSON.parse(sectorData);
        if (Array.isArray(parsed)) {
          sectorIds = parsed.map((s: any) => typeof s === 'object' ? (s.id || s.value) : s);
        }
      } catch {
        return [];
      }
    }

    if (sectorIds.length === 0) return [];

    // Build full paths for each sector ID
    return sectorIds
      .map(id => this.findSectorPath(id))
      .filter(path => path.length > 0);
  }

  // Find the full cascader path for a given sector ID
  findSectorPath(targetId: number): number[] {
    const findPath = (nodes: any[], currentPath: number[]): number[] | null => {
      for (const node of nodes) {
        const newPath = [...currentPath, node.value];
        if (node.value === targetId) {
          return newPath;
        }
        if (node.children && node.children.length > 0) {
          const found = findPath(node.children, newPath);
          if (found) return found;
        }
      }
      return null;
    };
    return findPath(this.sectorsTree, []) || [targetId];
  }

  // Handle sector selection change
  onSectorChange(value: any): void {
    this.businessSector = value || [];
    this.onCompanyNameChange();
  }

  onSectorDropdownVisible(visible: boolean): void {
    if (!visible) return;
    const host = this.businessSectorCascader?.nativeElement;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    if (rect.width) {
      const widthPx = `${Math.round(rect.width)}px`;
      this.sectorMenuStyle = {
        width: widthPx,
        'min-width': widthPx
      };
    }
  }

  get businessSectorDisplay(): string {
    const values = Array.isArray(this.businessSector) ? this.businessSector : [];
    const names = values
      .map((path: any) => {
        const id = Array.isArray(path) ? path[path.length - 1] : path;
        const numericId = Number(id);
        if (!Number.isFinite(numericId)) return '';
        return this.sectorNameMap.get(numericId) || '';
      })
      .filter(Boolean);
    return names.join(', ');
  }

  createUserDetail(): FormGroup {
    return this.fb.group({
      addUser: [''],
      selectRole: [''],
    });
  }

  get userDetail(): FormArray {
    return this.profileForm.get('userDetail') as FormArray;
  }

  addUser() {
    this.userDetail.push(this.createUserDetail());
  }

  removeUser(index: number) {
    this.userDetail.removeAt(index);
  }

  onSubmit(): void {
    this.sharedService.showLoader();

    if (!this.profileForm.valid) {
      this.sharedService.hideLoader();
      this.profileForm.markAllAsTouched();
      return;
    }

    const businessLogoPayload = this.prepareBusinessLogoPayload();

    if (this.companyData?.id) {
      this.updateCompanyProfile(businessLogoPayload);
    } else {
      this.createCompanyProfile(businessLogoPayload);
    }
  }

  /**
   * Prepares the business logo payload based on current state
   */
  private prepareBusinessLogoPayload(): any {
    const logoValue = this.profileForm.get('business_logo')?.value;

    // Case 1: New image uploaded (base64 string from FileReader)
    if (this.isNewBase64Image(logoValue)) {
      return [{
        path: logoValue,
        name: this.currentImage?.name || 'uploaded-image.png',
        type: 'image',
        mime: this.currentImage?.type || 'image/png',
        size: this.currentImage?.size || 0,
        meta: 0,
      }];
    }

    // Case 2: Existing image object from backend
    if (this.isExistingImageObject(logoValue)) {
      return [logoValue];
    }

    // Case 3: Image was removed
    if (this.isImageRemoved()) {
      return null;
    }

    // Case 4: No change - preserve existing backend image
    return this.companyData?.business_logo || null;
  }

  /**
   * Checks if the logo value is a new base64 image
   */
  private isNewBase64Image(logoValue: any): boolean {
    return logoValue &&
      typeof logoValue === 'string' &&
      logoValue.startsWith('data:image');
  }

  /**
   * Checks if the logo value is an existing image object from backend
   */
  private isExistingImageObject(logoValue: any): boolean {
    return logoValue &&
      typeof logoValue === 'object' &&
      logoValue.url;
  }

  /**
   * Checks if the image was removed
   */
  private isImageRemoved(): boolean {
    return this.imageSrc === null ||
      this.imageSrc === 'assets/images/profile.png';
  }

  /**
   * Updates existing company profile
   */
  private updateCompanyProfile(businessLogoPayload: any): void {
    const payload = {
      business_logo: businessLogoPayload,
      name: this.profileForm.get('name')?.value,
      address: this.profileForm.get('address')?.value,
      company_as_client_0_id: this.companyData.id,
    };

    this.pagesService
      .patch('SET_COMPANY_DETAILS', this.companyData.id, payload)
      .subscribe({
        next: (response) => {
          this.handleUpdateSuccess();
        },
        error: (error) => {
          this.handleError('update', error);
        },
      });
  }

  /**
   * Creates new company profile
   */
  private createCompanyProfile(businessLogoPayload: any): void {
    const payload = {
      business_logo: businessLogoPayload,
      name: this.profileForm.get('name')?.value,
      details: this.profileForm.get('address')?.value,
    };

    this.pagesService
      .update('SET_COMPANY_DETAILS', payload)
      .subscribe({
        next: (response) => {
          this.handleCreateSuccess();
        },
        error: (error) => {
          this.handleError('create', error);
        },
      });
  }

  /**
   * Handles successful update response
   */
  private handleUpdateSuccess(): void {
    this.sharedService.hideLoader();

    // ✅ Preserve uploaded image in localStorage before refreshing
    if (this.imageSrc && this.imageSrc !== 'assets/images/profile.png' && !this.imageSrc.includes('assets/images/profile')) {
      localStorage.setItem('profileImage', this.imageSrc);
      this.sharedService.updateProfileImage(this.imageSrc);
    }

    this.sharedService.createNotification(
      'success',
      this.translateService.instant('profile-page.profile-updated-msg')
    );
    this.getCompanyDetail();
  }

  /**
   * Handles successful create response
   */
  private handleCreateSuccess(): void {
    this.sharedService.hideLoader();

    // ✅ Preserve uploaded image in localStorage before refreshing
    if (this.imageSrc && this.imageSrc !== 'assets/images/profile.png' && !this.imageSrc.includes('assets/images/profile')) {
      localStorage.setItem('profileImage', this.imageSrc);
      this.sharedService.updateProfileImage(this.imageSrc);
    }

    this.sharedService.createNotification(
      'success',
      this.translateService.instant('profile-page.profile-created-msg')
    );
    this.getCompanyDetail();
    this.clearCompanyAlert();
  }

  /**
   * Clears company-related alerts from localStorage
   */
  private clearCompanyAlert(): void {
    const alertsJson = localStorage.getItem('app_alerts');
    if (!alertsJson) return;

    try {
      let alerts = JSON.parse(alertsJson);
      alerts = alerts.filter((alert: any) => alert.action !== 'company');
      localStorage.setItem('app_alerts', JSON.stringify(alerts));
      this.sharedService.closeAlertByAction('company');
    } catch (error) {
      console.error('Error clearing company alerts:', error);
    }
  }

  /**
   * Handles error responses
   */
  private handleError(operation: 'create' | 'update', error: any): void {
    this.sharedService.hideLoader();

    // Only show error notification for create operation
    if (operation === 'create') {
      this.sharedService.createNotification(
        'error',
        this.translateService.instant('profile-page.profile-update-failed')
      );
    }

    console.error(`Failed to ${operation} company profile:`, error);
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const profileControl = this.profileForm.get('business_logo');
    if (input && input.files && input.files[0]) {
      const file = input.files[0];

      if (profileControl) {
        profileControl.setErrors(null);
      }

      // Validate file type - only allow JPG and PNG
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        profileControl?.setErrors({ fileType: true });
        this.sharedService.createNotification(
          'error',
          this.translateService.instant('profile-page.image-type-invalid')
        );
        input.value = '';
        return;
      }

      const maxFileSize = 5 * 500 * 1024;
      if (file.size > maxFileSize) {
        profileControl?.setErrors({ fileSize: true });
        this.sharedService.createNotification(
          'error',
          this.translateService.instant('profile-page.image-size-invalid')
        );
        input.value = '';
        return;
      }

      // Open cropper modal
      this.imageChangedEvent = event;
      this.cropperModalVisible = true;
    }
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.blob ? URL.createObjectURL(event.blob) : '';
    // Convert blob to base64 for form submission
    if (event.blob) {
      const reader = new FileReader();
      reader.onload = () => {
        this.croppedImage = reader.result as string;
      };
      reader.readAsDataURL(event.blob);
    }
  }

  imageLoaded(image: LoadedImage) {
    // Image loaded in cropper
  }

  cropperReady() {
    // Cropper ready
  }

  loadImageFailed() {
    this.sharedService.createNotification('error', this.translateService.instant('profile-page.image-load-failed'));
    this.cropperModalVisible = false;
  }

  confirmCrop() {
    if (this.croppedImage) {
      const profileControl = this.profileForm.get('business_logo');
      this.imageSrc = this.croppedImage;
      profileControl?.setValue(this.croppedImage);
      this.profileForm.markAsDirty();
    }
    this.cropperModalVisible = false;
    this.imageChangedEvent = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  cancelCrop() {
    this.cropperModalVisible = false;
    this.imageChangedEvent = null;
    this.croppedImage = '';
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  resetPassword = {
    old: false,
    new: false,
    confirm: false,
  };

  toggleResetPasswordVisibility(field: 'old' | 'new' | 'confirm'): void {
    this.resetPassword[field] = !this.resetPassword[field];
  }

  removeImage(): void {
    this.imageSrc = null;
    this.currentImage = null;

    const profileControl = this.profileForm.get('business_logo');
    if (profileControl) {
      profileControl.setValue(null);
      profileControl.setErrors(null);
    }

    // localStorage clear
    localStorage.removeItem('profileImage');

    // default image set in shared service (isse subscription trigger hoga)
    this.sharedService.updateProfileImage('assets/images/profile.png');

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }



  // ✅ Real-time password strength validation
  onPasswordInput(): void {
    const password = this.resetPasswordForm.controls['new_password'].value || '';

    this.passwordStrength.lowercase = /[a-z]/.test(password);
    this.passwordStrength.uppercase = /[A-Z]/.test(password);
    this.passwordStrength.number = /\d/.test(password);
    this.passwordStrength.specialChar = /[@$!%*?&]/.test(password);
    this.passwordStrength.minLength = password.length >= 8;

    const allPassed = Object.values(this.passwordStrength).every(Boolean);
    const passwordControl = this.resetPasswordForm.controls['new_password'];

    if (!allPassed) {
      passwordControl.setErrors({ passwordWeak: true });
    } else {
      if (passwordControl.hasError('passwordWeak')) {
        passwordControl.setErrors(null);
      }
    }
  }

  // ✅ Enhanced canSubmit with all validations
  canSubmit(): boolean {
    const allPassed = Object.values(this.passwordStrength).every(Boolean);
    const formValid = this.resetPasswordForm.valid;

    // ✅ Check if old and new passwords are different
    const oldPassword = this.existingPasswordValue;
    const newPassword = this.resetPasswordForm.get('new_password')?.value;
    const passwordsDifferent = oldPassword !== newPassword;

    return formValid && allPassed && passwordsDifferent && this.isExistingPasswordVerified;
  }

  onResetPassword() {
    this.sharedService.showLoader();

    // ✅ Check if new password is the same as current password
    const oldPassword = this.existingPasswordValue;
    const newPassword = this.resetPasswordForm.get('new_password')?.value;

    if (oldPassword === newPassword) {
      this.sharedService.hideLoader();
      this.sharedService.createNotification(
        'error',
        this.translateService.instant('profile-page.new-password-same')
      );
      return;
    }

    if (this.canSubmit()) {
      const userDetails = this.authService.getUserDetails() || {};
      const email = userDetails?.email || '';

      const payload = {
        old_password: oldPassword,
        new_password: this.resetPasswordForm.value.new_password,
        user_email: email,
      };

      this.pagesService
        .resetPassword('USER_RESET_PASSWORD', payload)
        .subscribe({
          next: (response: any) => {
            this.sharedService.hideLoader();

            const output = response?.output;

            if (output?.isSuccess) {
              this.sharedService.createNotification(
                'success',
                output?.message || this.translateService.instant('profile-page.password-reset-success')
              );

              // Reset form and password strength
              this.resetPasswordFlow();

              setTimeout(() => {
                this.secureStorage.clearProtected();
                sessionStorage.removeItem('jwtToken');

                this.sharedService.createNotification(
                  'info',
                  this.translateService.instant('profile-page.login-new-password')
                );

                this.router.navigate(['/auth/login']);
              }, 2000);
            } else {
              // Show backend error message (e.g., incorrect existing password)
              this.sharedService.createNotification(
                'error',
                output?.message || this.translateService.instant('profile-page.password-reset-failed')
              );
            }
          },
          error: (error) => {
            this.sharedService.hideLoader();

            // Show backend error message
            const errorMessage = error?.error?.output?.message || error?.error?.message || this.translateService.instant('profile-page.password-reset-failed');
            this.sharedService.createNotification('error', errorMessage);
          },
        });
    } else {
      this.sharedService.hideLoader();
      this.resetPasswordForm.markAllAsTouched();
    }
  }

  // ✅ Custom validator for password matching
  passwordMatchValidator(formGroup: FormGroup) {
    const password = formGroup.get('new_password')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordsMismatch: true };
  }

  // Verify existing password with backend
  verifyExistingPassword() {
    if (!this.existingPasswordValue.trim()) {
      this.sharedService.createNotification('error', this.translateService.instant('profile-page.enter-existing-password'));
      return;
    }

    this.isVerifyingPassword = true;
    this.sharedService.showLoader();

    const userDetails = this.authService.getUserDetails() || {};
    const email = userDetails?.email || '';

    // Send same password to verify without actually changing it
    const payload = {
      old_password: this.existingPasswordValue,
      new_password: this.existingPasswordValue, // Same password
      user_email: email,
    };

    this.pagesService
      .resetPassword('USER_RESET_PASSWORD', payload)
      .subscribe({
        next: (response: any) => {
          this.isVerifyingPassword = false;
          this.sharedService.hideLoader();

          const output = response?.output;

          // Check if response indicates old password was correct
          // Even if backend says "same password" error, we know old password is right
          if (output?.isSuccess === false) {
            // Check if error is about same password (means old password was correct)
            const message = output?.message?.toLowerCase() || '';
            if (message.includes('same') || message.includes('different')) {
              // Old password was correct, show next fields
              this.isExistingPasswordVerified = true;
              this.resetPasswordForm.patchValue({
                oldPassword: this.existingPasswordValue
              });
            } else {
              // Old password was wrong
              this.sharedService.createNotification('error', output?.message || this.translateService.instant('profile-page.incorrect-existing-password'));
              this.isExistingPasswordVerified = false;
            }
          } else {
            // Success means old password was correct
            this.isExistingPasswordVerified = true;
            this.resetPasswordForm.patchValue({
              oldPassword: this.existingPasswordValue
            });
          }
        },
        error: (error) => {
          this.isVerifyingPassword = false;
          this.sharedService.hideLoader();

          // Check error message from backend
          const errorMessage = error?.error?.output?.message || error?.error?.message || '';
          const messageLower = errorMessage.toLowerCase();

          // If error is about same password, old password was correct
          if (messageLower.includes('same') || messageLower.includes('different')) {
            this.isExistingPasswordVerified = true;
            this.resetPasswordForm.patchValue({
              oldPassword: this.existingPasswordValue
            });
          } else {
            // Old password is wrong
            this.sharedService.createNotification('error', errorMessage || this.translateService.instant('profile-page.incorrect-existing-password'));
            this.isExistingPasswordVerified = false;
          }
        },
      });
  }

  // Reset password flow
  resetPasswordFlow() {
    this.isExistingPasswordVerified = false;
    this.existingPasswordValue = '';
    this.resetPasswordForm.reset();
    this.initializePasswordStrength();
  }

  // Image Modal Methods
  openImageModal(imageUrl: string): void {
    if (imageUrl) {
      this.imageModalImages = [imageUrl];
      this.imageModalCurrentIndex = 0;
      this.imageModalVisible = true;
    }
  }

  closeImageModal(): void {
    this.imageModalVisible = false;
    this.imageModalImages = [];
    this.imageModalCurrentIndex = 0;
  }

  onImageIndexChange(index: number): void {
    this.imageModalCurrentIndex = index;
  }

  hasUnsavedChanges(): boolean {
    return (this.isEditMode && this.profileForm?.dirty) || this.isDescriptionChanged;
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const isRefresh = event.key === 'F5' || ((event.ctrlKey || event.metaKey) && event.key === 'r');
    if (isRefresh && this.hasUnsavedChanges()) {
      event.preventDefault();
      this.modal.confirm({
        nzTitle: this.translateService.instant('profile-page.unsaved-title'),
        nzContent: this.translateService.instant('profile-page.unsaved-refresh-content'),
        nzOkText: this.translateService.instant('profile-page.yes-refresh'),
        nzCancelText: this.translateService.instant('common.cancel'),
        nzCentered: true,
        nzClassName: 'contest-styled-modal',
        nzOnOk: () => window.location.reload(),
        nzOnCancel: () => {}
      });
    }
  }

  confirmUnsavedChanges(): Promise<boolean> {
    return new Promise((resolve) => {
      this.modal.confirm({
        nzTitle: this.translateService.instant('profile-page.unsaved-title'),
        nzContent: this.translateService.instant('profile-page.unsaved-leave-content'),
        nzOkText: this.translateService.instant('profile-page.yes-leave'),
        nzCancelText: this.translateService.instant('common.cancel'),
        nzCentered: true,
        nzClassName: 'contest-styled-modal',
        nzOnOk: () => resolve(true),
        nzOnCancel: () => resolve(false)
      });
    });
  }

}
